// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Btn from '../claims/shared/Btn';
import Icon from '../claims/shared/Icon';
import Modal from '../claims/shared/Modal';
import PdfViewerModal from '../documents/PdfViewerModal';
import {
  getLetterSections,
  approveLetter,
  startNewVersion,
  getPreviewPdfBlob,
  downloadFinalPdf,
} from '../../api/letterSectionsApi';
import LetterActionBar from './LetterActionBar';
import RegenerationBlock from './RegenerationBlock';
import UnifiedLetterEditor from './UnifiedLetterEditor';

const PREVIEW_ERROR_COPY = "Couldn't load the preview. Try again.";

const LOCKED_STATUSES = ['approved', 'pdf_generated'];

/**
 * Drop-in structured-letter panel (LTR-08): action bar + ONE unified
 * rich-text editor (UnifiedLetterEditor) over all sections + a section-picker
 * RegenerationBlock + approval-flow modals + PDF preview/download. Mounted into
 * the three host surfaces; regeneration endpoints stay host-owned via
 * onRegenerateSection.
 *
 * CLAUDE.md effect safety: the fetch effect is keyed by letterType + letterId
 * + sectionsRefreshKey (+ an internal refresh counter for approve/new-version)
 * — never by callback identity. Callbacks live in refs.
 *
 * @param {{
 *   letterType: string,
 *   letterId: string,
 *   sectionsRefreshKey?: number,
 *   generationStatus?: string,
 *   onRegenerateSection?: (sectionKey: string) => void,
 *   onAlert?: (message: string) => void,
 * }} props
 */
export default function LetterSectionsPanel({
  letterType,
  letterId,
  sectionsRefreshKey = 0,
  generationStatus,
  onRegenerateSection,
  onAlert,
}) {
  const [letter, setLetter] = useState(null);
  const [sections, setSections] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [internalRefresh, setInternalRefresh] = useState(0);
  const [saveStates, setSaveStates] = useState({});
  const [approveOpen, setApproveOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [startingNewVersion, setStartingNewVersion] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const onAlertRef = useRef(onAlert);
  const onRegenerateSectionRef = useRef(onRegenerateSection);
  onAlertRef.current = onAlert;
  onRegenerateSectionRef.current = onRegenerateSection;

  // Fetch keyed by stable resource identity + explicit refresh keys only.
  useEffect(() => {
    if (!letterType || !letterId) return undefined;
    // While the letter is still generating, its sections don't exist yet — fetching
    // now would 404 and show a spurious "Couldn't load" error. The generating branch
    // renders a spinner; this effect re-runs (generationStatus is a dep) and fetches
    // once generation reaches a terminal state.
    if (generationStatus === 'generating') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await getLetterSections(letterType, letterId);
        if (cancelled) return;
        setLetter(data?.letter || null);
        const list = Array.isArray(data?.sections) ? [...data.sections] : [];
        list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        setSections(list);
        setLoaded(true);
      } catch (err) {
        if (cancelled) return;
        setLoaded(true);
        onAlertRef.current?.("Couldn't load the letter sections. Try again.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [letterType, letterId, sectionsRefreshKey, internalRefresh, generationStatus]);

  const refetch = useCallback(() => setInternalRefresh((n) => n + 1), []);

  const resolveVariable = useCallback(
    (key) => letter?.context_snapshot?.[key] ?? '',
    [letter]
  );

  const handleSaveStateChange = useCallback((sectionId, state) => {
    setSaveStates((prev) => (prev[sectionId] === state ? prev : { ...prev, [sectionId]: state }));
  }, []);

  const handleRequestNewVersion = useCallback(() => {
    setGateOpen(true);
  }, []);

  const handleRegenerateSection = useCallback((sectionKey) => {
    onRegenerateSectionRef.current?.(sectionKey);
  }, []);

  const handleApproveConfirm = useCallback(async () => {
    setApproveOpen(false);
    setApproving(true);
    try {
      await approveLetter(letterType, letterId);
      refetch();
    } catch (err) {
      onAlertRef.current?.("Couldn't approve the letter. Try again.");
    } finally {
      setApproving(false);
    }
  }, [letterType, letterId, refetch]);

  const handleStartNewVersion = useCallback(async () => {
    setGateOpen(false);
    setStartingNewVersion(true);
    try {
      await startNewVersion(letterType, letterId);
      refetch();
    } catch (err) {
      onAlertRef.current?.("Couldn't start a new version. Try again.");
    } finally {
      setStartingNewVersion(false);
    }
  }, [letterType, letterId, refetch]);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewUrl((current) => {
      if (current) window.URL.revokeObjectURL(current);
      return null;
    });
  }, []);

  const handlePreview = useCallback(async () => {
    try {
      const blob = await getPreviewPdfBlob(letterType, letterId);
      const url = window.URL.createObjectURL(
        blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' })
      );
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (err) {
      onAlertRef.current?.(PREVIEW_ERROR_COPY);
    }
  }, [letterType, letterId]);

  const handleDownload = useCallback(() => {
    const version = letter?.version;
    const filename =
      letterType === 'lmn' ? `lmn-v${version}.pdf` : `appeal-letter-v${version}.pdf`;
    Promise.resolve(downloadFinalPdf(letterType, letterId, filename)).catch(() => {
      onAlertRef.current?.("Couldn't download the final PDF. Try again.");
    });
  }, [letterType, letterId, letter]);

  const locked = LOCKED_STATUSES.includes(letter?.approval_status);
  const savingCount = Object.values(saveStates).filter((s) => s === 'saving').length;
  // 06-06 new-version preserves approved_at as last-approval history — a
  // draft letter with approval history means "edited after approval".
  const needsReapproval = letter?.approval_status === 'draft' && Boolean(letter?.approved_at);
  const nextVersion = (letter?.version ?? 0) + 1;

  if (generationStatus === 'generating') {
    return (
      <div className="ab" style={{ textAlign: 'center', padding: 32 }}>
        <Icon name="loader-2" size={24} className="ab-spin" />
        <div
          style={{
            marginTop: 12,
            fontSize: 'var(--ab-text-h4)',
            fontWeight: 'var(--ab-weight-semi)',
            color: 'var(--ab-fg)',
          }}
        >
          Generating letter…
        </div>
        <div style={{ marginTop: 8, fontSize: 'var(--ab-text-xs)', color: 'var(--ab-fg-subtle)' }}>
          This usually takes under a minute. Sections will appear when ready.
        </div>
      </div>
    );
  }

  if (loaded && sections.length === 0) {
    return (
      <div className="ab" style={{ textAlign: 'center', padding: 32 }}>
        <div
          style={{
            fontSize: 'var(--ab-text-h4)',
            fontWeight: 'var(--ab-weight-semi)',
            color: 'var(--ab-fg)',
          }}
        >
          No letter yet
        </div>
        <div style={{ marginTop: 8, fontSize: 'var(--ab-text-sm)', color: 'var(--ab-fg-subtle)' }}>
          Generate the letter to create editable sections from this claim&apos;s data.
        </div>
      </div>
    );
  }

  if (!loaded) return null;

  return (
    <div className="ab">
      {needsReapproval && (
        <div
          style={{
            background: 'var(--ab-pending-soft)',
            border: '1px solid var(--ab-pending-ring)',
            borderRadius: 'var(--ab-radius-sm)',
            color: 'var(--ab-pending)',
            fontSize: 'var(--ab-text-sm)',
            padding: '8px 16px',
            marginBottom: 16,
          }}
        >
          Edited after approval — reapproval required before download.
        </div>
      )}

      <LetterActionBar
        letter={letter}
        sectionsCount={sections.length}
        sectionsSavingCount={savingCount}
        approving={approving}
        onPreview={handlePreview}
        onApprove={() => setApproveOpen(true)}
        onDownload={handleDownload}
      />

      <div style={{ marginTop: 32 }}>
        <UnifiedLetterEditor
          sections={sections}
          letterType={letterType}
          locked={locked}
          resolveVariable={resolveVariable}
          onSaveStateChange={handleSaveStateChange}
          onRequestNewVersion={handleRequestNewVersion}
          onAlert={onAlert}
        />
        <RegenerationBlock
          sections={sections}
          locked={locked}
          generating={generationStatus === 'generating'}
          onRegenerateSection={handleRegenerateSection}
        />
      </div>

      <Modal
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        title="Approve letter?"
        width={480}
        footer={
          <>
            <Btn kind="secondary" onClick={() => setApproveOpen(false)}>
              Keep editing
            </Btn>
            <Btn kind="primary" onClick={handleApproveConfirm}>
              Approve letter
            </Btn>
          </>
        }
      >
        <div style={{ fontSize: 'var(--ab-text-body)', color: 'var(--ab-fg)' }}>
          Approving locks the current content and generates the final PDF. Editing afterward
          creates a new version that needs reapproval.
        </div>
      </Modal>

      <Modal
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        title="Start a new version?"
        width={480}
        footer={
          <>
            <Btn kind="secondary" onClick={() => setGateOpen(false)}>
              Go back
            </Btn>
            {/* Secondary, not destructive — the approved version is kept. */}
            <Btn kind="secondary" onClick={handleStartNewVersion} disabled={startingNewVersion}>
              Start new version
            </Btn>
          </>
        }
      >
        <div style={{ fontSize: 'var(--ab-text-body)', color: 'var(--ab-fg)' }}>
          {`This letter is approved. Editing creates version ${nextVersion} as a draft — reapproval is required before the final PDF can be downloaded.`}
        </div>
      </Modal>

      <PdfViewerModal
        open={previewOpen}
        onClose={closePreview}
        src={previewUrl}
        title="Letter preview"
        documentLabel="Letter"
      />
    </div>
  );
}
